import React, { useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { gql } from '@/modes/waterSupplier/gql';
import { AsyncSelect } from '../ReactSelect';
import { getNameLikeCondition } from '../../helpers/search';
import { HazardLevel } from './HazardLevelPicker';
import StatusChip from '../Status/StatusChip';
import { Flex, Box, Text } from 'rebass';
import chroma from 'chroma-js';
import colors from '../../theme/colors';
import StatusDot from '../Status/StatusDot';
import { ResultOf } from 'gql.tada';

export const FETCH_WATER_HAZARDS = gql(`
  query GetPickerWaterHazards($where: JSON, $orderBy: [ColumnOrder]) {
    waterHazards(where: $where, limit: null, orderBy: $orderBy) {
      id
      name
      description
      hazardLevel
    }
  }
`);

export type WaterHazard = ResultOf<
  typeof FETCH_WATER_HAZARDS
>['waterHazards'][number];

type WaterHazardPickerProps = {
  waterHazardIds?: string[];
  onChange?: (waterHazard: readonly WaterHazard[] | WaterHazard[]) => void;
  placeholder?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  excludeIds?: string[];
};

const WaterHazardsPicker = ({
  waterHazardIds,
  // where,
  onChange,
  placeholder = 'Select hazards...',
  isClearable,
  isDisabled,
  autoFocus,
  excludeIds,
}: WaterHazardPickerProps): JSX.Element => {
  const excludeSet = new Set(excludeIds ?? []);
  // where = where ?? {};
  const client = useApolloClient();

  const { error: itemError, data: itemData } = useQuery(FETCH_WATER_HAZARDS, {
    variables: {
      where: {
        id: waterHazardIds,
      },
    },
    skip: !waterHazardIds,
  });

  const fetchedSelected: WaterHazard[] | null = itemData?.waterHazards ?? null;
  const [internalSelected, setInternalSelected] = useState<WaterHazard[]>([]);

  // Use fetched data when waterHazardIds provided, otherwise track internally
  const selected = waterHazardIds ? fetchedSelected : internalSelected;

  if (itemError) {
    throw itemError;
  }

  const loadOptionsFunc = async (inputValue: string) => {
    const inputMatch = inputValue
      ? {
          name: getNameLikeCondition(inputValue),
        }
      : undefined;

    const res = await client.query({
      query: FETCH_WATER_HAZARDS,
      fetchPolicy: 'network-only',
      variables: {
        where: {
          ...inputMatch,
        },
        limit: null,
        orderBy: inputValue
          ? (undefined as any)
          : [{ col: 'name', dir: 'ASC' }],
      },
    });

    if (res.data && res.data.waterHazards) {
      return res.data.waterHazards.filter(
        (h: WaterHazard) => !excludeSet.has(h.id)
      );
    }

    return [];
  };

  return (
    <AsyncSelect
      autoFocus={autoFocus}
      isMulti
      value={selected}
      loadOptions={loadOptionsFunc}
      defaultOptions
      isClearable={isClearable}
      isDisabled={isDisabled}
      noOptionsMessage={() => null}
      menuPortalTarget={document.body}
      menuPlacement="auto"
      styles={{
        menu: (base) => ({
          ...base,
          minWidth: '250px',
        }),
        multiValue: (styles, { data }) => {
          return {
            ...styles,
            background:
              data.hazardLevel === 'HIGH'
                ? chroma(colors.statusError).alpha(0.3).hex()
                : chroma(colors.statusOrange).alpha(0.3).hex(),
          };
        },
      }}
      onChange={(val) => {
        if (!waterHazardIds) {
          const arr = Array.isArray(val) ? [...val] : val ? [val] : [];
          setInternalSelected(arr as WaterHazard[]);
        }
        onChange?.(val as readonly WaterHazard[]);
      }}
      placeholder={placeholder ?? 'Hazard name...'}
      formatOptionLabel={(option, { context }) => (
        <Flex flexDirection="column">
          <Flex flexDirection="row" alignItems="center">
            <StatusDot
              status={option.hazardLevel === 'HIGH' ? 'error' : 'orange'}
            />
            <Box ml={1} dangerouslySetInnerHTML={{ __html: option.name }} />
          </Flex>
          {context === 'menu' && option.description && (
            <Text
              fontSize={0}
              color="gray"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {option.description}
            </Text>
          )}
        </Flex>
      )}
      getOptionValue={(waterHazard) => waterHazard.id}
    />
  );
};

export default WaterHazardsPicker;
